<script setup lang="ts">
import { Skeleton } from '@/ui/skeleton'

export type AppSkeletonType =
  | 'form'
  | 'integration'
  | 'breadcrumb'
  | 'default'
  | 'section'
  | 'subheader'
  | 'text'
  | 'button'
  | 'thumbnail'
  | 'image'
  | 'cardImgRight'
  | 'cardImgLeft'
  | 'list'
  | 'tableCell'
  | 'tableAvatarCell'

withDefaults(
  defineProps<{
    type?: AppSkeletonType
    sectionHeight?: string
  }>(),
  {
    type: undefined,
    sectionHeight: 'h-48'
  }
)
</script>

<template>
  <div class="flex flex-col">
    <div v-if="type === 'form'">
      <div class="space-y-6">
        <div v-for="i in 4" :key="i" class="pt-5 sm:grid sm:grid-cols-3 sm:items-start sm:gap-4">
          <Skeleton class="block h-2 bg-stone-300 sm:mt-px sm:pt-2" />
          <Skeleton class="mt-1 h-2 bg-stone-300 sm:col-span-2 sm:mt-0">
            <Skeleton class="block h-2 max-w-lg bg-stone-300" />
          </Skeleton>
        </div>
      </div>
    </div>

    <div v-else-if="type === 'integration'">
      <div class="px-16 py-20">
        <div class="space-y-4">
          <div class="mb-12 flex items-center gap-4">
            <Skeleton class="h-40 w-32 bg-stone-300" />
            <Skeleton class="h-12 w-full bg-stone-300" />
          </div>
          <div v-for="i in 6" :key="i" class="sm:col-span-6 sm:grid">
            <div class="mb-2">
              <div class="relative">
                <Skeleton class="h-3 w-32 bg-stone-300" />
                <Skeleton class="my-1 h-3 bg-stone-300" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="type === 'breadcrumb'">
      <div>
        <ol class="flex items-center space-x-4">
          <li v-for="i in 2" :key="i">
            <div class="flex items-center space-x-4">
              <Skeleton class="h-2 w-12 bg-stone-300" />
              <svg
                v-if="i !== 2"
                class="h-5 w-5 shrink-0 text-foreground-disabled"
                xmlns="http://www.w3.org/2000/svg"
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
              </svg>
            </div>
          </li>
        </ol>
        <Skeleton class="mt-5 h-4 w-44 bg-stone-300" />
      </div>
    </div>

    <div v-else-if="type === 'default'">
      <Skeleton class="mt-8 h-8 w-full" />
      <Skeleton class="mt-8 h-8 w-full" />
      <Skeleton class="mt-8 h-96 w-full" />
    </div>

    <Skeleton v-else-if="type === 'section'" :class="['w-full', sectionHeight]" />

    <div v-else-if="type === 'subheader'">
      <Skeleton class="mb-2 mt-10 h-6 w-36 md:w-56" />
      <Skeleton class="mb-2 h-4 w-48 md:w-80" />
      <Skeleton class="mb-2 h-4 w-64 md:w-96" />
      <Skeleton class="mb-2 h-4 w-60 md:w-64" />
    </div>

    <Skeleton
      v-else-if="type === 'text'"
      class="relative inline-flex h-full min-h-5 items-center justify-center text-sm font-medium"
    />

    <Skeleton v-else-if="type === 'button'" class="h-10 w-44" />

    <div v-else-if="type === 'thumbnail'">
      <Skeleton class="relative flex h-32 w-52 items-center justify-center">
        <Skeleton class="h-16 w-36 bg-white" />
      </Skeleton>
    </div>

    <Skeleton v-else-if="type === 'image'" class="relative flex h-full w-full items-center justify-center rounded-sm">
      <BaseIcon name="HeroPhotoIconSolid" class="w-1/2 min-w-5 max-w-xs text-white" />
    </Skeleton>

    <div v-else-if="type === 'cardImgRight' || type === 'cardImgLeft'">
      <ul class="gap-6 lg:grid lg:grid-cols-6 lg:py-6">
        <li v-for="i in 4" :key="i" class="rounded-md lg:col-span-3 lg:mx-0 lg:border lg:border-border">
          <div class="flex px-4 py-6 lg:py-4" :class="type === 'cardImgLeft' ? 'flex-row-reverse' : 'space-x-6'">
            <div class="flex flex-auto flex-col" :class="type === 'cardImgRight' ? 'justify-between' : 'space-y-4'">
              <Skeleton v-if="type === 'cardImgRight'" class="h-4 w-32 rounded-sm" />
              <Skeleton v-if="type === 'cardImgRight'" class="h-4 w-12 rounded-sm" />
              <Skeleton class="h-4 w-full rounded-sm" />
              <Skeleton class="h-4 w-full rounded-sm" />
            </div>
            <Skeleton class="relative h-28 w-28 flex-none" :class="type === 'cardImgLeft' && 'mr-6'" />
          </div>
        </li>
      </ul>
    </div>

    <div v-else-if="type === 'list'" class="p-4">
      <ul class="space-y-3">
        <li v-for="i in 4" :key="i" class="rounded-md lg:col-span-3 lg:mx-0 lg:border lg:border-border">
          <Skeleton class="h-8 w-full rounded-sm" />
        </li>
      </ul>
    </div>

    <div v-else-if="type === 'tableCell'" class="p-4">
      <div class="py-2">
        <Skeleton class="block h-3 w-36 bg-stone-300" />
      </div>
    </div>

    <div v-else-if="type === 'tableAvatarCell'" class="p-4">
      <div class="py-2">
        <div class="flex items-center">
          <Skeleton class="block h-12 w-12 rounded-full bg-stone-300" />
          <div class="ml-4">
            <div class="flex-col space-y-2 pt-3">
              <Skeleton class="h-3 w-32 bg-stone-300" />
              <Skeleton class="h-1.5 w-32 bg-stone-300" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
      <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
        <div class="overflow-hidden border-b sm:rounded-lg">
          <table class="min-w-full divide-y divide-border">
            <thead class="bg-muted">
              <tr>
                <th v-for="i in 6" :key="i" scope="col" class="px-6 py-3">
                  <Skeleton class="block h-2 w-full bg-stone-300" />
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-border bg-white">
              <tr v-for="i in 4" :key="i">
                <td class="px-6 py-4">
                  <div class="flex items-center">
                    <Skeleton class="block h-10 w-10 rounded-full bg-stone-300" />
                    <div class="ml-4 flex items-center">
                      <Skeleton class="h-3 w-32 bg-stone-300" />
                    </div>
                  </div>
                </td>
                <td v-for="j in 5" :key="j" class="px-6 py-4">
                  <Skeleton class="block h-3 w-36 bg-stone-300" />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>
